import { useState, useEffect, useCallback } from "react";
import { Tables } from "../../database.types";
import {
  gameService,
  GameStateRow,
  RoundRow,
  QuestionRow,
  RoundLifelineRow,
  BonusRoundRow,
  ClassicBonusAnswerRow,
  ClassicBonusTeamResponseRow,
  ClosestGuessQuestionRow,
} from "../data/gameService";
import { teamService } from "../data/teamService";
import { SupabaseClient } from "@supabase/supabase-js";

export const useGame = (
  supabase: SupabaseClient,
  gameId: string | undefined,
) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [game, setGame] = useState<Tables<"games"> | null>(null);
  const [gameState, setGameState] = useState<GameStateRow | null>(null);
  const [rounds, setRounds] = useState<RoundRow[]>([]);
  const [questions, setQuestions] = useState<QuestionRow[]>([]);
  const [lifelines, setLifelines] = useState<RoundLifelineRow[]>([]);
  const [bonusRounds, setBonusRounds] = useState<BonusRoundRow[]>([]);
  const [classicBonusAnswers, setClassicBonusAnswers] = useState<
    ClassicBonusAnswerRow[]
  >([]);
  const [classicBonusTeamResponses, setClassicBonusTeamResponses] = useState<
    ClassicBonusTeamResponseRow[]
  >([]);
  const [closestGuessQuestions, setClosestGuessQuestions] = useState<
    ClosestGuessQuestionRow[]
  >([]);
  const [team1, setTeam1] = useState<Tables<"teams"> | null>(null);
  const [team2, setTeam2] = useState<Tables<"teams"> | null>(null);
  const [team1LogoUrl, setTeam1LogoUrl] = useState<string | null>(null);
  const [team2LogoUrl, setTeam2LogoUrl] = useState<string | null>(null);

  const fetchFullGameData = useCallback(async () => {
    if (!gameId) return;
    try {
      const fullGame = await gameService.getFullGame(supabase, gameId);
      if (!fullGame) return;

      setGame(fullGame.game);
      setGameState(fullGame.gameState);
      setRounds(fullGame.rounds);
      setQuestions(fullGame.questions);
      setLifelines(fullGame.lifelines);
      setBonusRounds(fullGame.bonusRounds);
      setClassicBonusAnswers(fullGame.classicBonusAnswers);
      setClassicBonusTeamResponses(fullGame.classicBonusTeamResponses);
      setClosestGuessQuestions(fullGame.closestGuessQuestions);
    } catch (err) {
      console.error("Error fetching full game data:", err);
    }
  }, [gameId, supabase]);

  const fetchGame = useCallback(async () => {
    try {
      if (!gameId) return;
      setLoading(true);

      const gameRes = await gameService.getById(supabase, gameId);
      if (gameRes && gameRes.team_one_id && gameRes.team_two_id) {
        const [team1Res, team2Res] = await Promise.all([
          teamService.getById(supabase, gameRes.team_one_id),
          teamService.getById(supabase, gameRes.team_two_id),
        ]);
        setTeam1(team1Res);
        setTeam2(team2Res);
        setGame(gameRes);

        // Fetch full normalized game data
        await fetchFullGameData();
      } else {
        setTeam1(null);
        setTeam2(null);
        setGame(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [gameId, supabase, fetchFullGameData]);

  // Setup real-time subscription to game_state changes
  const setupSubscription = useCallback(() => {
    if (!gameId) return;

    const channelName = `game-state-changes:${gameId}`;

    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "game_state",
          filter: `game_id=eq.${gameId}`,
        },
        (payload) => {
          console.log("game_state change:", payload);
          if (payload.eventType === "DELETE") {
            setGameState(null);
          } else {
            // Update gameState from payload
            setGameState(payload.new as GameStateRow);
            // Re-fetch all other data to stay in sync
            fetchFullGameData();
          }
        },
      )
      .subscribe((status) => {
        console.log("game_state subscription status:", status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [gameId, supabase, fetchFullGameData]);

  const fetchLogo = useCallback(
    async (isTeam1: boolean) => {
      try {
        const logoId = isTeam1 ? team1?.logo_id : team2?.logo_id;
        if (logoId) {
          const { data, error } = await supabase.storage
            .from("dozen-attachments")
            .createSignedUrl(`attachments/${logoId}`, 3600);

          if (error) {
            console.error("Error fetching logo URL:", error);
            return;
          }

          if (data?.signedUrl) {
            if (isTeam1) {
              setTeam1LogoUrl(data.signedUrl);
            } else {
              setTeam2LogoUrl(data.signedUrl);
            }
          }
        }
      } catch (error) {
        // handle error
      }
    },
    [team1, team2, supabase],
  );

  // Fetch initial game data
  useEffect(() => {
    if (gameId) {
      fetchGame();
    }
  }, [gameId, fetchGame]);

  // Setup subscription when gameId is available
  useEffect(() => {
    if (gameId) {
      const cleanup = setupSubscription();
      return cleanup;
    }
  }, [gameId, setupSubscription]);

  // Fetch team logos when team data is available
  useEffect(() => {
    if (team1) fetchLogo(true);
  }, [team1, fetchLogo]);

  useEffect(() => {
    if (team2) fetchLogo(false);
  }, [team2, fetchLogo]);

  return {
    loading,
    game,
    gameState,
    rounds,
    questions,
    lifelines,
    bonusRounds,
    classicBonusAnswers,
    classicBonusTeamResponses,
    closestGuessQuestions,
    team1,
    team2,
    team1LogoUrl,
    team2LogoUrl,
  };
};
